import { useState, type ReactNode } from "react";
import { CheckCircle2, Info } from "lucide-react";
import {
  useBuildingDocumentStats,
  useLatestUploadDate,
  shortestRangeContaining,
  type GroupBy,
  type TimeRange,
} from "@/hooks/buildings/useBuildingDocumentStats";
import { DocumentsByGroupChart } from "@/components/charts/DocumentsByGroupChart";
import { UploadActivityChart } from "@/components/charts/UploadActivityChart";
import { DocumentsPerProjectChart } from "@/components/charts/DocumentsPerProjectChart";
import { DashboardPanel } from "@/components/building/DashboardPanel";
import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { Translations } from "@/lib/i18n/types";

const GROUP_KEYS: GroupBy[] = ["category", "discipline", "stage"];

interface DocumentsSectionProps {
  buildingId: string;
  /** When set, all dashboard stats/charts are scoped to this project
   * (`building_projects.id`). Undefined = whole building. */
  projectId?: string;
  /** Optional right-hand column of the second row ("Uploaded today"), passed by
   * the project dashboard. That page is always single-project scope, so the
   * per-project chart is never present alongside it. */
  uploadedToday?: ReactNode;
  t: Translations;
}

function StatField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-[13px] font-medium text-neutral-700">{value}</span>
    </div>
  );
}

export function DocumentsSection({
  buildingId,
  projectId,
  uploadedToday,
  t,
}: DocumentsSectionProps) {
  // Auto-select the shortest range (30d → 90d → 1y) that actually contains
  // uploads so users don't land on an empty chart by default; a manual pick
  // wins over the derived default. The pick is tied to the building/project it
  // was made on — this section stays mounted across scope switches, so an
  // unscoped pick would silently override the next scope's derived default.
  const scope = `${buildingId}:${projectId ?? ""}`;
  const [rangePick, setRangePick] = useState<{ scope: string; range: TimeRange } | null>(null);
  const latestUpload = useLatestUploadDate(buildingId, projectId);
  const userTimeRange = rangePick?.scope === scope ? rangePick.range : null;
  const timeRange = userTimeRange ?? shortestRangeContaining(latestUpload.data);
  const d = t.buildingDetail.dashboard;

  const stats = useBuildingDocumentStats(buildingId, { timeRange, projectId });

  const showByGroupChart = GROUP_KEYS.some((g) => stats.byGroup[g].length > 0);
  const showPerProjectChart = stats.perProject.length >= 2;

  // Always shown when this section renders (BuildingView only mounts it once
  // the building has documents), so the card and its 30d/90d/1y range toggle
  // stay reachable for every building — including ones whose uploads all fall
  // on a single day or predate the default 30-day window. The chart handles
  // sparse data itself: a single bar for one upload day, a "No data" message
  // for an empty range.
  const uploadChart = (
    <UploadActivityChart
      data={stats.uploadActivity}
      userBreakdown={stats.uploadByUser}
      timeRange={timeRange}
      onTimeRangeChange={(range) => setRangePick({ scope, range })}
      isLoading={stats.isUploadActivityLoading}
      t={t}
    />
  );

  return (
    <div>
      {/* Label row: uppercase section label, stats right-aligned — on the
          white page outside the card. */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <SettingsSectionLabel>{d.documents}</SettingsSectionLabel>
        <div className="flex items-center gap-4">
          {/* Two counters saying the same number carry no signal — collapse to
              one "all processed" status; split only while processing is
              actually pending. */}
          {stats.total > 0 && stats.processed === stats.total ? (
            <span className="flex items-center gap-1.5 text-[13px] font-medium text-neutral-700">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
              {stats.total === 1
                ? d.fileAllProcessedOne
                : d.filesAllProcessed.replace("{count}", String(stats.total))}
            </span>
          ) : (
            <>
              <StatField label={d.total} value={String(stats.total)} />
              <StatField label={d.processed} value={String(stats.processed)} />
            </>
          )}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={d.unsupportedTypesTooltip}
                  className="inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-neutral-400"
                >
                  <Info size={12} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">
                {d.unsupportedTypesTooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* All charts share one dashboard card, in two rows. Row one:
          "Documents by" — the three groupings as donuts across the full
          width. Row two: either Upload activity | hairline | Uploaded today
          (the project dashboard), or Documents per project | Upload activity
          (the per-project chart only applies with 2+ projects in scope —
          without it upload activity takes the row alone). */}
      <DashboardPanel>
        {showByGroupChart && (
          <DocumentsByGroupChart data={stats.byGroup} isLoading={stats.isByGroupLoading} t={t} />
        )}
        <div className={cn(showByGroupChart && "mt-8 border-t border-neutral-200 pt-6")}>
          {uploadedToday ? (
            // Chart first in the DOM, so the columns stack chart-above-feed on
            // narrow screens; the hairline only exists in the two-column layout.
            <div className="grid grid-cols-1 gap-y-8 lg:grid-cols-[minmax(0,55fr)_minmax(0,45fr)]">
              <div className="lg:pr-10">{uploadChart}</div>
              <div className="lg:border-l lg:border-neutral-200 lg:pl-10">{uploadedToday}</div>
            </div>
          ) : (
            <div
              className={cn(
                showPerProjectChart && "grid grid-cols-1 gap-y-8 lg:grid-cols-2 lg:gap-x-10"
              )}
            >
              {showPerProjectChart && (
                <DocumentsPerProjectChart
                  data={stats.perProject}
                  categoryBreakdown={stats.projectCategories}
                  isLoading={stats.isPerProjectLoading}
                  t={t}
                />
              )}
              {uploadChart}
            </div>
          )}
        </div>
      </DashboardPanel>
    </div>
  );
}

/**
 * The section's title alone, with an "i" explaining why there is nothing
 * under it: the viewer sees only part of the project, and analytics over that
 * part would read as the project's own numbers. No charts, no empty cards.
 */
export function DocumentAnalyticsUnavailable({ t }: { t: Translations }) {
  const d = t.buildingDetail.dashboard;
  return (
    <div className="mb-3 flex items-center gap-1.5" data-testid="document-analytics-unavailable">
      <SettingsSectionLabel>{d.documents}</SettingsSectionLabel>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={d.analyticsPartialAccess}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Info className="h-3.5 w-3.5" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{d.analyticsPartialAccess}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
